/**
 * Status pill.
 *
 * The tone comes from `@mfa/domain`'s status metadata, so a status renders the
 * same colour on the donor list, the tracking header and the admin table —
 * the inconsistency recorded as gap G-06 cannot recur.
 */

import { memo } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  orderStatusMeta,
  paymentStatusMeta,
  type OrderStatus,
  type PaymentStatus,
} from '@mfa/domain';
import { colors, radius, space, statusTone, type StatusTone } from '../../theme';
import { Text } from './Text';
import { useT } from '../../core/i18n/LanguageProvider';

export interface BadgeProps {
  label: string;
  tone?: StatusTone;
  testID?: string;
}

export const Badge = memo(function Badge({ label, tone = 'neutral', testID }: BadgeProps) {
  const t = useT();
  const palette = statusTone[tone];
  const text = t(label);
  return (
    <View
      testID={testID}
      style={[styles.pill, { backgroundColor: palette.bg }]}
      accessibilityRole="text"
      accessibilityLabel={`${t('Status')}: ${text}`}
    >
      <Text variant="badge" style={{ color: palette.fg }} numberOfLines={1}>
        {text}
      </Text>
    </View>
  );
});

/** Badge for an order status, labelled and coloured from shared metadata. */
export const OrderStatusBadge = memo(function OrderStatusBadge({
  status,
  testID,
}: {
  status: OrderStatus;
  testID?: string;
}) {
  const meta = orderStatusMeta[status];
  return <Badge label={meta.label} tone={meta.tone} testID={testID} />;
});

export const PaymentStatusBadge = memo(function PaymentStatusBadge({
  status,
  testID,
}: {
  status: PaymentStatus;
  testID?: string;
}) {
  const meta = paymentStatusMeta[status];
  return <Badge label={meta.label} tone={meta.tone} testID={testID} />;
});

const styles = StyleSheet.create({
  pill: {
    paddingHorizontal: space.md,
    paddingVertical: space.xs,
    borderRadius: radius.full,
    alignSelf: 'flex-start',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border.subtle,
  },
});
